import { useState, useEffect, useRef } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  Autocomplete,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  AddInfoFormRanDb,
  createSingleSiteRanDb,
} from './functions/create_single_site_ran_db.function';
import { formUploadRanDb } from './functions/form_upload_ran_db.function';
import { useBackdrop } from '../../common/components/backDrop_context.component';
import { notify } from '../../common/functions/react-toastify.function';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  selectedRanDbRow,
  openAddInfoFormRanDb,
  allSitesRanDb,
  ranDbViewMode,
  ranDbArchivedType,
  ranDbTotalCount,
  ranDbTotalPages,
  ranDbCurrentPage,
  ranDbPageSize,
} from '../../common/atom/atoms';
import { getAllRanSites } from './functions/get_all_ran_sites.function';
import SiteHistoryPanel from './site_history_panel.component';

const defaultValues = {
  site_code: '',
  physical: '',
  locked_status: '',
  vendor: '',
  region: '',
  coverage_type: '',
  lat: null,
  long: null,
  site_status: '',
  pta_tehsil: '',
  pta_district: '',
  pta_province: '',
  prefix: '',
  city_prefix_based: '',
  technical_city: '',
  commercial_district: '',
  commercial_city: '',
  mbu: '',
  franchise_id: null,
  site_type: '',
  sharing_type_hostguest: '',
  omo_name: '',
  b2s_id: '',
  bsc: '',
  bsc_code: '',
  lte_technology: '',
  deodar_tag: '',
  // Commented out fields - keeping for future reference
  // '2g': '',
  // '4g': '',
  // part_of_dec_cfo: '',
  // scope: null,
  // pta_remarks: null,
  // usf_site: '',
  // commercial_subregion: '',
  // commercial_region: '',
  // '4g_capacity': '',
  // '2g_locked_tag': null,
  // '4g_locked_tag': null,
  // '2g_cells': null,
  // '4g_cells': null,
  // l1800a_cells: null,
  // l1800b_cells: null,
  // mmcells: null,
  // total_l18_ab_cells: null,
  // l2100_cells: null,
  // l900a_cells: null,
  // total_trx: null,
  // '900_trx': null,
  // '1800_trx': null,
};

const options = {
  physical: ['2G', '3G', '4G', '2G+4G'],
  locked_status: ['Locked', 'Temporary Locked'],
  vendor: ['Huawei', 'Ericsson', 'ZTE'],
  region: ['North', 'South', 'Central A', 'Central B'],
  coverage_type: ['Urban', 'Rural', 'Macro'],
  site_status: ['Active', 'Inactive'],
  pta_tehsil: ['LIAQUATPUR TEHSIL', 'Tehsil A', 'Tehsil B'],
  pta_district: ['RAHIM YAR KHAN', 'District X', 'District Y'],
  pta_province: ['PUNJAB', 'Province A', 'Province B'],
  prefix: ['ABB', 'Prefix A', 'Prefix B'],
  city_prefix_based: ['Abbasia', 'City A', 'City B'],
  technical_city: ['Rural', 'Urban', 'Tech City A'],
  commercial_district: ['Rahim Yar Khan', 'District A', 'District B'],
  commercial_city: ['Liaqatpur_Rural', 'City A', 'City B'],
  mbu: ['C7-KNP-06', 'MBU 1', 'MBU 2'],
  site_type: ['Tower co B2S', 'Type A', 'Type B'],
  sharing_type_hostguest: ['Host', 'Guest'],
  omo_name: ['Enfrashare', 'OMO A', 'OMO B'],
  b2s_id: ['ES2-LQP-01977', 'B2S-001', 'B2S-002'],
  bsc: ['RYK317Z', 'BSC A', 'BSC B'],
  bsc_code: ['RYK317Z_MGW_2', 'CODE-001', 'CODE-002'],
  lte_technology: ['LTE', 'LTE-A', 'LTE-Pro', '5G'],
  deodar_tag: ['Tag A', 'Tag B', 'Tag C'],
  // Commented out options - keeping for future reference
  // '2g': ['2G', '3G', '4G'],
  // '4g': ['2G', '3G', '4G'],
  // part_of_dec_cfo: ['Yes', 'No'],
  // usf_site: ['Yes', 'No'],
  // commercial_subregion: ['Central 7', 'Subregion 1', 'Subregion 2'],
  // commercial_region: ['Central B', 'Region 1', 'Region 2'],
  // '4g_capacity': ['2T2R', 'Capacity A', 'Capacity B'],
};

const fieldLabels = {
  physical: 'Physical',
  locked_status: 'Locked Status',
  vendor: 'Vendor',
  region: 'Region',
  coverage_type: 'Coverage Type',
  site_status: 'Site Status',
  pta_tehsil: 'PTA Tehsil',
  pta_district: 'PTA District',
  pta_province: 'PTA Province',
  prefix: 'Prefix',
  city_prefix_based: 'City (prefix based)',
  technical_city: 'Technical City',
  commercial_district: 'Commercial District',
  commercial_city: 'Commercial City',
  mbu: 'MBU',
  franchise_id: 'Franchise ID',
  site_type: 'Site Type',
  sharing_type_hostguest: 'Sharing Type (Host/Guest)',
  omo_name: 'OMO Name',
  b2s_id: 'B2S ID',
  bsc: 'BSC',
  bsc_code: 'BSC Code',
  lte_technology: 'LTE Technology',
  deodar_tag: 'Deodar Tag',
  // Commented out labels - keeping for future reference
  // '2g': '2G',
  // '4g': '4G',
  // part_of_dec_cfo: 'Part of DEC CFO',
  // usf_site: 'USF Site',
  // commercial_subregion: 'Commercial Subregion',
  // commercial_region: 'Commercial Region',
  // '4g_capacity': '4G Capacity',
};

const RanDBAddSingleSiteComponent = () => {
  const [formState, setFormState] = useState(defaultValues);
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const selectedRow = useAtomValue(selectedRanDbRow);
  const setSelectedRow = useSetAtom(selectedRanDbRow);
  const setOpenAddInfoForm = useSetAtom(openAddInfoFormRanDb);
  const setAllRanSite = useSetAtom(allSitesRanDb);
  const setTotalCount = useSetAtom(ranDbTotalCount);
  const setTotalPages = useSetAtom(ranDbTotalPages);
  const setCurrentPage = useSetAtom(ranDbCurrentPage);
  const setPageSize = useSetAtom(ranDbPageSize);
  const viewMode = useAtomValue(ranDbViewMode);
  const archivedType = useAtomValue(ranDbArchivedType);
  const isEditMode = !!selectedRow;
  const autocompleteRefs = useRef({});
  const [auditLogData, setAuditLogData] = useState(null);
  const [changedFields, setChangedFields] = useState({});

  // Populate form with selected row data when editing
  useEffect(() => {
    if (selectedRow) {
      const mappedData = {
        site_code: selectedRow.site_code || '',
        physical: selectedRow.physical || '',
        locked_status: selectedRow.locked_status || '',
        vendor: selectedRow.vendor || '',
        region: selectedRow.region || '',
        coverage_type: selectedRow.coverage_type || '',
        lat: selectedRow.lat || null,
        long: selectedRow.long || null,
        site_status: selectedRow.site_status || '',
        pta_tehsil: selectedRow.pta_tehsil || '',
        pta_district: selectedRow.pta_district || '',
        pta_province: selectedRow.pta_province || '',
        prefix: selectedRow.prefix || '',
        city_prefix_based: selectedRow.city_prefix_based || '',
        technical_city: selectedRow.technical_city || '',
        commercial_district: selectedRow.commercial_district || '',
        commercial_city: selectedRow.commercial_city || '',
        mbu: selectedRow.mbu || '',
        franchise_id: selectedRow.franchise_id || null,
        site_type: selectedRow.site_type || '',
        sharing_type_hostguest: selectedRow.sharing_type_hostguest || '',
        omo_name: selectedRow.omo_name || '',
        b2s_id: selectedRow.b2s_id || '',
        bsc: selectedRow.bsc || '',
        bsc_code: selectedRow.bsc_code || '',
        lte_technology: selectedRow.lte_technology || '',
        deodar_tag: selectedRow.deodar_tag || '',
        // Commented out fields - keeping for future reference
        // '2g': selectedRow['2g'] || '',
        // '4g': selectedRow['4g'] || '',
        // part_of_dec_cfo: selectedRow.part_of_dec_cfo || '',
        // scope: selectedRow.scope || null,
        // pta_remarks: selectedRow.pta_remarks || null,
        // usf_site: selectedRow.usf_site || '',
        // commercial_subregion: selectedRow.commercial_subregion || '',
        // commercial_region: selectedRow.commercial_region || '',
        // '4g_capacity': selectedRow['4g_capacity'] || '',
        // '2g_locked_tag': selectedRow['2g_locked_tag'] || null,
        // '4g_locked_tag': selectedRow['4g_locked_tag'] || null,
        // '2g_cells': selectedRow['2g_cells'] || null,
        // '4g_cells': selectedRow['4g_cells'] || null,
        // l1800a_cells: selectedRow.l1800a_cells || null,
        // l1800b_cells: selectedRow.l1800b_cells || null,
        // mmcells: selectedRow.mmcells || null,
        // total_l18_ab_cells: selectedRow.total_l18_ab_cells || null,
        // l2100_cells: selectedRow.l2100_cells || null,
        // l900a_cells: selectedRow.l900a_cells || null,
        // total_trx: selectedRow.total_trx || null,
        // '900_trx': selectedRow['900_trx'] || null,
        // '1800_trx': selectedRow['1800_trx'] || null,
      };
      setFormState(mappedData);
    }
  }, [selectedRow]);

  // Cleanup effect to prevent focus errors
  useEffect(() => {
    return () => {
      // Cleanup function to handle component unmounting
      // This prevents focus errors when component is destroyed
    };
  }, []);

  const fetchAllRanSites = async () => {
    showBackdrop();
    try {
      let filters = {};
      if (viewMode === 'archived') {
        if (archivedType === 'all') {
          filters = {
            archived: true,
          };
        } else if (archivedType === 'Dismantle') {
          filters = {
            // archived: true,
            archived_type: 'Dismantled',
          };
        } else if (archivedType === 'Relocation') {
          filters = {
            // archived: true,
            archived_type: 'Relocation',
          };
        } else if (archivedType === 'Other') {
          filters = {
            // archived: true,
            archived_type: 'Other',
          };
        }
      } else {
        filters = {
          archived: false,
        };
      }
      const resp = await getAllRanSites(filters);
      setAllRanSite(resp?.data || []);
      if (resp?.pagination) {
        setTotalCount(resp.pagination.totalCount);
        setTotalPages(resp.pagination.totalPages);
        setCurrentPage(resp.pagination.currentPage);
        setPageSize(resp.pagination.limit);
      }
    } catch (error) {
      console.error('Error fetching RanDB sites:', error);
    }
    hideBackdrop();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleAutocompleteChange = (name, value) => {
    try {
      setFormState((prev) => ({ ...prev, [name]: value || '' }));
    } catch (error) {
      console.error('Error in handleAutocompleteChange:', error);
      // Fallback to prevent crash
      setFormState((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleClear = () => {
    setFormState(defaultValues);
    clearAuditState();
    if (isEditMode) {
      setSelectedRow(null);
    }
  };

  const getFieldStyle = (fieldName) => {
    if (changedFields[fieldName]) {
      return {
        position: 'relative',
        '&::before': {
          // content: `"Original: ${changedFields[fieldName]?.original || 'N/A'}"`,
          position: 'absolute',
          top: '-20px',
          left: '0',
          fontSize: '10px',
          color: '#e65100',
          fontWeight: 600,
          backgroundColor: '#fff3cd',
          padding: '2px 6px',
          borderRadius: '4px',
          // border: '1px solid #ffc107',
          zIndex: 1,
          whiteSpace: 'nowrap',
        },
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#fff3cd',
          // border: '2px solid #ffc107',
          borderRadius: '8px',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: '#fff8e1',
            // border: '2px solid #ffb300',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(255, 193, 7, 0.3)',
          },
          '&.Mui-focused': {
            backgroundColor: '#fff8e1',
            border: '2px solid #ffb300',
            boxShadow: '0 0 0 3px rgba(255, 193, 7, 0.3)',
            transform: 'translateY(-1px)',
          },
        },
        '& .MuiInputLabel-root': {
          color: '#e65100',
          fontWeight: 700,
          fontSize: '0.875rem',
          '&.Mui-focused': {
            color: '#e65100',
            fontWeight: 700,
          },
        },
        '& .MuiInputBase-input': {
          color: '#e65100',
          fontWeight: 600,
          fontSize: '0.875rem',
        },
        '& .MuiAutocomplete-input': {
          color: '#e65100',
          fontWeight: 600,
        },
        '& .MuiChip-root': {
          backgroundColor: '#fff8e1',
          color: '#e65100',
          border: '1px solid #ffc107',
        },
      };
    }

    // Special styling for locked status field
    if (fieldName === 'locked_status') {
      return {
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#c9f2f2', // Very light blue background
          '&:hover': {
            backgroundColor: '#c9f2f2',
          },
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
          },
        },
      };
    }

    return {};
  };

  const restoreOriginalData = () => {
    if (auditLogData) {
      setFormState(auditLogData);
      setChangedFields({});
    }
  };

  const clearAuditState = () => {
    setAuditLogData(null);
    setChangedFields({});
  };

  const handleAuditLogClick = (auditLog, siteIndex = 0) => {
    console.log('Audit log clicked:', auditLog, 'Site index:', siteIndex);
    if (
      auditLog.details &&
      auditLog.details.updatedSites &&
      auditLog.details.updatedSites[siteIndex]
    ) {
      const auditSiteData = auditLog.details.updatedSites[siteIndex];

      // Map audit log data to form state
      const mappedAuditData = {
        site_code: auditSiteData.site_code || '',
        physical: auditSiteData.physical || '',
        locked_status: auditSiteData.locked_status || '',
        vendor: auditSiteData.vendor || '',
        region: auditSiteData.region || '',
        coverage_type: auditSiteData.coverage_type || '',
        lat: auditSiteData.lat || null,
        long: auditSiteData.long || null,
        site_status: auditSiteData.site_status || '',
        pta_tehsil: auditSiteData.pta_tehsil || '',
        pta_district: auditSiteData.pta_district || '',
        pta_province: auditSiteData.pta_province || '',
        prefix: auditSiteData.prefix || '',
        city_prefix_based: auditSiteData.city_prefix_based || '',
        technical_city: auditSiteData.technical_city || '',
        commercial_district: auditSiteData.commercial_district || '',
        commercial_city: auditSiteData.commercial_city || '',
        mbu: auditSiteData.mbu || '',
        franchise_id: auditSiteData.franchise_id || null,
        site_type: auditSiteData.site_type || '',
        sharing_type_hostguest: auditSiteData.sharing_type_hostguest || '',
        omo_name: auditSiteData.omo_name || '',
        b2s_id: auditSiteData.b2s_id || '',
        bsc: auditSiteData.bsc || '',
        bsc_code: auditSiteData.bsc_code || '',
        lte_technology: auditSiteData.lte_technology || '',
        deodar_tag: auditSiteData.deodar_tag || '',
        // Commented out fields - keeping for future reference
        // '2g': auditSiteData['2g'] || '',
        // '4g': auditSiteData['4g'] || '',
        // part_of_dec_cfo: auditSiteData.part_of_dec_cfo || '',
        // scope: auditSiteData.scope || null,
        // pta_remarks: auditSiteData.pta_remarks || null,
        // usf_site: auditSiteData.usf_site || '',
        // commercial_subregion: auditSiteData.commercial_subregion || '',
        // commercial_region: auditSiteData.commercial_region || '',
        // '4g_capacity': auditSiteData['4g_capacity'] || '',
        // '2g_locked_tag': auditSiteData['2g_locked_tag'] || null,
        // '4g_locked_tag': auditSiteData['4g_locked_tag'] || null,
        // '2g_cells': auditSiteData['2g_cells'] || null,
        // '4g_cells': auditSiteData['4g_cells'] || null,
        // l1800a_cells: auditSiteData.l1800a_cells || null,
        // l1800b_cells: auditSiteData.l1800b_cells || null,
        // mmcells: auditSiteData.mmcells || null,
        // total_l18_ab_cells: auditSiteData.total_l18_ab_cells || null,
        // l2100_cells: auditSiteData.l2100_cells || null,
        // l900a_cells: auditSiteData.l900a_cells || null,
        // total_trx: auditSiteData.total_trx || null,
        // '900_trx': auditSiteData['900_trx'] || null,
        // '1800_trx': auditSiteData['1800_trx'] || null,
      };

      // Store original data if not already stored
      if (!auditLogData) {
        setAuditLogData(formState);
      }

      // Update form with audit log data
      setFormState(mappedAuditData);

      // Compare with original data to find changed fields
      const changes = {};
      Object.keys(mappedAuditData).forEach((key) => {
        const originalValue = selectedRow[key];
        const auditValue = mappedAuditData[key];

        // Convert to strings for comparison, handling null/undefined
        const originalStr = originalValue?.toString() || '';
        const auditStr = auditValue?.toString() || '';

        if (originalStr !== auditStr) {
          changes[key] = {
            original: originalValue,
            changed: auditValue,
          };
        }
      });

      console.log('Changed fields:', changes);
      setChangedFields(changes);
    }
  };

  // Add maxLength map for text fields
  const maxLengths = {
    site_code: 50,
    vendor: 30,
    region: 30,
    coverage_type: 30,
    part_of_dec_cfo: 10,
    scope: 30,
    commercial_district: 50,
    commercial_city: 50,
    mbu: 30,
    commercial_subregion: 30,
    commercial_region: 30,
    pta_tehsil: 50,
    pta_district: 50,
    pta_province: 50,
    prefix: 10,
    city_prefix_based: 30,
    technical_city: 30,
    site_type: 30,
    sharing_type_hostguest: 10,
    omo_name: 30,
    b2s_id: 30,
    bsc: 30,
    bsc_code: 30,
    '4g_capacity': 20,
  };

  const handleSubmit = async () => {
    if (!formState.site_code || formState.site_code.trim() === '') {
      notify('error', 'Site Code is required');
      return;
    }
    // Validate maxLength for text fields
    for (const key in maxLengths) {
      if (formState[key] && formState[key].length > maxLengths[key]) {
        notify('error', `${fieldLabels[key] || key} exceeds max length of ${maxLengths[key]}`);
        return;
      }
    }
    // Validate number fields
    const numberFields = [
      'lat',
      'long',
      'franchise_id',
      '2g_cells',
      '4g_cells',
      'l1800a_cells',
      'l1800b_cells',
      'mmcells',
      'total_l18_ab_cells',
      'l2100_cells',
      'l900a_cells',
      'total_trx',
      '900_trx',
      '1800_trx',
    ];
    // for (const key of numberFields) {
    //   if (formState[key] !== null && formState[key] !== undefined && formState[key] !== '') {
    //     const value = Number(formState[key]);
    //     if (isNaN(value) || value < 0 || value > 999999) {
    //       notify('error', `${fieldLabels[key] || key} must be a number between 0 and 999999`);
    //       return;
    //     }
    //   }
    // }
    try {
      showBackdrop();
      let resp;

      if (isEditMode) {
        resp = await formUploadRanDb(formState, selectedRow?.id);
        if (resp?.response) {
          notify('success', 'Updated Successfully');
          // Refresh the data after successful update
          await fetchAllRanSites();
          // Clear selected row and close form
          setSelectedRow(null);
          setOpenAddInfoForm(false);
          // Clear the form
          handleClear();
          clearAuditState();
        }
      } else {
        resp = await createSingleSiteRanDb(formState);
        if (resp?.response) {
          notify('success', 'Saved Successfully');
          // Refresh the data after successful creation
          await fetchAllRanSites();
          // Clear the form after successful save
          handleClear();
          clearAuditState();
        }
      }
      hideBackdrop();
    } catch (error) {
      hideBackdrop();
      notify('error', error.message);
    }
  };

  return (
    <>
      {isEditMode ? (
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          width="100%"
          height="100%"
          gap={2}
        >
          <Box flex={7} minWidth={0}>
            <div
              style={{
                width: '100%',
                height: '100%',
                background: 'white',
                boxShadow: '0px 0px 30px 2px rgba(0, 0, 0, 0.05)',
                borderRadius: 8,
                padding: 24,
                marginTop: '1%',
              }}
            >
              {/* Show changed fields indicator */}
              {Object.keys(changedFields).length > 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginBottom: '16px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#e65100',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>⚠️</span>
                        {Object.keys(changedFields).length} field(s) have changed
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={restoreOriginalData}
                      sx={{
                        borderColor: '#ffc107',
                        color: '#e65100',
                        '&:hover': {
                          borderColor: '#ffb300',
                          backgroundColor: '#fff8e1',
                        },
                      }}
                    >
                      Restore Latest
                    </Button>
                  </Box>
                </Grid>
              )}
              <Grid container spacing={2}>
                {/* Site Code */}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Site Code"
                    name="site_code"
                    value={formState.site_code}
                    onChange={handleChange}
                    size="small"
                    required
                    disabled={isEditMode}
                    sx={getFieldStyle('site_code')}
                  />
                </Grid>

                {/* Physical */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.physical = el)}
                    options={options.physical}
                    value={formState.physical || null}
                    onChange={(e, value) => handleAutocompleteChange('physical', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Physical"
                        fullWidth
                        sx={getFieldStyle('physical')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Locked Status - Special colorful field */}
                <Grid item xs={4}>
                  <FormControl fullWidth size="small" sx={getFieldStyle('locked_status')}>
                    <InputLabel>Locked Status</InputLabel>
                    <Select
                      value={formState.locked_status || ''}
                      onChange={handleChange}
                      name="locked_status"
                      label="Locked Status"
                    >
                      {options.locked_status.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          sx={{
                            fontSize: '0.875rem',
                            color: '#333333',
                            padding: '8px 12px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                              color: '#1976d2',
                            },
                            '&.Mui-selected': {
                              backgroundColor: '#e3f2fd !important',
                              color: '#1976d2',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: '#bbdefb !important',
                              },
                            },
                          }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Vendor */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.vendor = el)}
                    options={options.vendor}
                    value={formState.vendor || null}
                    onChange={(e, value) => handleAutocompleteChange('vendor', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vendor"
                        fullWidth
                        sx={getFieldStyle('vendor')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Region */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.region = el)}
                    options={options.region}
                    value={formState.region || null}
                    onChange={(e, value) => handleAutocompleteChange('region', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Region"
                        fullWidth
                        sx={getFieldStyle('region')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Coverage Type */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.coverage_type = el)}
                    options={options.coverage_type}
                    value={formState.coverage_type || null}
                    onChange={(e, value) => handleAutocompleteChange('coverage_type', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Coverage Type"
                        fullWidth
                        sx={getFieldStyle('coverage_type')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Latitude */}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Lat"
                    name="lat"
                    type="number"
                    value={formState.lat || ''}
                    onChange={handleChange}
                    size="small"
                    sx={getFieldStyle('lat')}
                  />
                </Grid>

                {/* Longitude */}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Long"
                    name="long"
                    type="number"
                    value={formState.long || ''}
                    onChange={handleChange}
                    size="small"
                    sx={getFieldStyle('long')}
                  />
                </Grid>

                {/* Site Status */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.site_status = el)}
                    options={options.site_status}
                    value={formState.site_status || null}
                    onChange={(e, value) => handleAutocompleteChange('site_status', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Site Status"
                        fullWidth
                        sx={getFieldStyle('site_status')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* PTA Tehsil */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.pta_tehsil = el)}
                    options={options.pta_tehsil}
                    value={formState.pta_tehsil || null}
                    onChange={(e, value) => handleAutocompleteChange('pta_tehsil', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="PTA Tehsil"
                        fullWidth
                        sx={getFieldStyle('pta_tehsil')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* PTA District */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.pta_district = el)}
                    options={options.pta_district}
                    value={formState.pta_district || null}
                    onChange={(e, value) => handleAutocompleteChange('pta_district', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="PTA District"
                        fullWidth
                        sx={getFieldStyle('pta_district')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* PTA Province */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.pta_province = el)}
                    options={options.pta_province}
                    value={formState.pta_province || null}
                    onChange={(e, value) => handleAutocompleteChange('pta_province', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="PTA Province"
                        fullWidth
                        sx={getFieldStyle('pta_province')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Prefix */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.prefix = el)}
                    options={options.prefix}
                    value={formState.prefix || null}
                    onChange={(e, value) => handleAutocompleteChange('prefix', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Prefix"
                        fullWidth
                        sx={getFieldStyle('prefix')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* City (prefix based) */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.city_prefix_based = el)}
                    options={options.city_prefix_based}
                    value={formState.city_prefix_based || null}
                    onChange={(e, value) => handleAutocompleteChange('city_prefix_based', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City (prefix based)"
                        fullWidth
                        sx={getFieldStyle('city_prefix_based')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Technical City */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.technical_city = el)}
                    options={options.technical_city}
                    value={formState.technical_city || null}
                    onChange={(e, value) => handleAutocompleteChange('technical_city', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Technical City"
                        fullWidth
                        sx={getFieldStyle('technical_city')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Commercial District */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.commercial_district = el)}
                    options={options.commercial_district}
                    value={formState.commercial_district || null}
                    onChange={(e, value) => handleAutocompleteChange('commercial_district', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Commercial District"
                        fullWidth
                        sx={getFieldStyle('commercial_district')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Commercial City */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.commercial_city = el)}
                    options={options.commercial_city}
                    value={formState.commercial_city || null}
                    onChange={(e, value) => handleAutocompleteChange('commercial_city', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Commercial City"
                        fullWidth
                        sx={getFieldStyle('commercial_city')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* MBU */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.mbu = el)}
                    options={options.mbu}
                    value={formState.mbu || null}
                    onChange={(e, value) => handleAutocompleteChange('mbu', value)}
                    renderInput={(params) => (
                      <TextField {...params} label="MBU" fullWidth sx={getFieldStyle('mbu')} />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Franchise ID */}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Franchise ID"
                    name="franchise_id"
                    type="number"
                    value={formState.franchise_id || ''}
                    onChange={handleChange}
                    size="small"
                    sx={getFieldStyle('franchise_id')}
                  />
                </Grid>

                {/* Site Type */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.site_type = el)}
                    options={options.site_type}
                    value={formState.site_type || null}
                    onChange={(e, value) => handleAutocompleteChange('site_type', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Site Type"
                        fullWidth
                        sx={getFieldStyle('site_type')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Sharing Type (Host/Guest) */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.sharing_type_hostguest = el)}
                    options={options.sharing_type_hostguest}
                    value={formState.sharing_type_hostguest || null}
                    onChange={(e, value) =>
                      handleAutocompleteChange('sharing_type_hostguest', value)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sharing Type (Host/Guest)"
                        fullWidth
                        sx={getFieldStyle('sharing_type_hostguest')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* OMO Name */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.omo_name = el)}
                    options={options.omo_name}
                    value={formState.omo_name || null}
                    onChange={(e, value) => handleAutocompleteChange('omo_name', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="OMO Name"
                        fullWidth
                        sx={getFieldStyle('omo_name')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* B2S ID */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.b2s_id = el)}
                    options={options.b2s_id}
                    value={formState.b2s_id || null}
                    onChange={(e, value) => handleAutocompleteChange('b2s_id', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="B2S ID"
                        fullWidth
                        sx={getFieldStyle('b2s_id')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* BSC */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.bsc = el)}
                    options={options.bsc}
                    value={formState.bsc || null}
                    onChange={(e, value) => handleAutocompleteChange('bsc', value)}
                    renderInput={(params) => (
                      <TextField {...params} label="BSC" fullWidth sx={getFieldStyle('bsc')} />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* BSC Code */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.bsc_code = el)}
                    options={options.bsc_code}
                    value={formState.bsc_code || null}
                    onChange={(e, value) => handleAutocompleteChange('bsc_code', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="BSC Code"
                        fullWidth
                        sx={getFieldStyle('bsc_code')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* LTE Technology */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.lte_technology = el)}
                    options={options.lte_technology}
                    value={formState.lte_technology || null}
                    onChange={(e, value) => handleAutocompleteChange('lte_technology', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="LTE Technology"
                        fullWidth
                        sx={getFieldStyle('lte_technology')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                {/* Deodar Tag */}
                <Grid item xs={4}>
                  <Autocomplete
                    ref={(el) => (autocompleteRefs.current.deodar_tag = el)}
                    options={options.deodar_tag}
                    value={formState.deodar_tag || null}
                    onChange={(e, value) => handleAutocompleteChange('deodar_tag', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Deodar Tag"
                        fullWidth
                        sx={getFieldStyle('deodar_tag')}
                      />
                    )}
                    size="small"
                    blurOnSelect={false}
                    clearOnBlur={false}
                    selectOnFocus={false}
                    freeSolo={false}
                    disableClearable={false}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent={'end'} gap={2}>
                    {Object.keys(changedFields).length > 0 && (
                      <>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ff6b35',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            mr: 2,
                          }}
                        >
                          {Object.keys(changedFields).length} field(s) changed
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={restoreOriginalData}
                          sx={{
                            borderColor: '#ff6b35',
                            color: '#ff6b35',
                            '&:hover': {
                              borderColor: '#e55a2b',
                              backgroundColor: '#fff5f2',
                            },
                          }}
                        >
                          Restore Latest
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outlined"
                      onClick={handleClear}
                      sx={{
                        borderColor: '#2B323B',
                        color: '#2B323B',
                      }}
                    >
                      Clear
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!formState.site_code || formState.site_code.trim() === ''}
                      sx={{
                        backgroundColor: '#BD1D23',
                        color: '#ffffff',
                        fontWeight: 600,
                        padding: '8px 24px',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        boxShadow: '0 2px 4px rgba(189, 29, 35, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: '#a0181e',
                          boxShadow: '0 4px 8px rgba(189, 29, 35, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                          boxShadow: '0 2px 4px rgba(189, 29, 35, 0.3)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                          color: '#9e9e9e',
                          boxShadow: 'none',
                          border: '1px solid #e0e0e0',
                          cursor: 'not-allowed',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            transform: 'none',
                            boxShadow: 'none',
                          },
                        },
                      }}
                    >
                      {isEditMode ? 'Update' : 'Save'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </div>
          </Box>
          <Box flex={3} minWidth={250} maxWidth={400}>
            <SiteHistoryPanel
              siteCode={formState.site_code}
              onAuditLogClick={handleAuditLogClick}
              onClose={clearAuditState}
            />
          </Box>
        </Box>
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            background: 'white',
            boxShadow: '0px 0px 30px 2px rgba(0, 0, 0, 0.05)',
            borderRadius: 8,
            padding: 24,
            marginTop: '1%',
          }}
        >
          <Grid container spacing={2}>
            {/* Site Code */}
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Site Code"
                name="site_code"
                value={formState.site_code}
                onChange={handleChange}
                size="small"
                required
                disabled={isEditMode}
              />
            </Grid>

            {/* Physical */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.physical = el)}
                options={options.physical}
                value={formState.physical || null}
                onChange={(e, value) => handleAutocompleteChange('physical', value)}
                renderInput={(params) => <TextField {...params} label="Physical" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Locked Status - Special elegant field */}
            <Grid item xs={4}>
              <FormControl fullWidth size="small" sx={getFieldStyle('locked_status')}>
                <InputLabel>Locked Status</InputLabel>
                <Select
                  value={formState.locked_status || ''}
                  onChange={handleChange}
                  name="locked_status"
                  label="Locked Status"
                >
                  {options.locked_status.map((option) => (
                    <MenuItem
                      key={option}
                      value={option}
                      sx={{
                        fontSize: '0.875rem',
                        color: '#333333',
                        padding: '8px 12px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          color: '#1976d2',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#e3f2fd !important',
                          color: '#1976d2',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#bbdefb !important',
                          },
                        },
                      }}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Vendor */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.vendor = el)}
                options={options.vendor}
                value={formState.vendor || null}
                onChange={(e, value) => handleAutocompleteChange('vendor', value)}
                renderInput={(params) => <TextField {...params} label="Vendor" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Region */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.region = el)}
                options={options.region}
                value={formState.region || null}
                onChange={(e, value) => handleAutocompleteChange('region', value)}
                renderInput={(params) => <TextField {...params} label="Region" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Coverage Type */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.coverage_type = el)}
                options={options.coverage_type}
                value={formState.coverage_type || null}
                onChange={(e, value) => handleAutocompleteChange('coverage_type', value)}
                renderInput={(params) => <TextField {...params} label="Coverage Type" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Latitude */}
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Lat"
                name="lat"
                type="number"
                value={formState.lat || ''}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            {/* Longitude */}
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Long"
                name="long"
                type="number"
                value={formState.long || ''}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            {/* Site Status */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.site_status = el)}
                options={options.site_status}
                value={formState.site_status || null}
                onChange={(e, value) => handleAutocompleteChange('site_status', value)}
                renderInput={(params) => <TextField {...params} label="Site Status" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* PTA Tehsil */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.pta_tehsil = el)}
                options={options.pta_tehsil}
                value={formState.pta_tehsil || null}
                onChange={(e, value) => handleAutocompleteChange('pta_tehsil', value)}
                renderInput={(params) => <TextField {...params} label="PTA Tehsil" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* PTA District */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.pta_district = el)}
                options={options.pta_district}
                value={formState.pta_district || null}
                onChange={(e, value) => handleAutocompleteChange('pta_district', value)}
                renderInput={(params) => <TextField {...params} label="PTA District" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* PTA Province */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.pta_province = el)}
                options={options.pta_province}
                value={formState.pta_province || null}
                onChange={(e, value) => handleAutocompleteChange('pta_province', value)}
                renderInput={(params) => <TextField {...params} label="PTA Province" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Prefix */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.prefix = el)}
                options={options.prefix}
                value={formState.prefix || null}
                onChange={(e, value) => handleAutocompleteChange('prefix', value)}
                renderInput={(params) => <TextField {...params} label="Prefix" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* City (prefix based) */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.city_prefix_based = el)}
                options={options.city_prefix_based}
                value={formState.city_prefix_based || null}
                onChange={(e, value) => handleAutocompleteChange('city_prefix_based', value)}
                renderInput={(params) => (
                  <TextField {...params} label="City (prefix based)" fullWidth />
                )}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Technical City */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.technical_city = el)}
                options={options.technical_city}
                value={formState.technical_city || null}
                onChange={(e, value) => handleAutocompleteChange('technical_city', value)}
                renderInput={(params) => <TextField {...params} label="Technical City" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Commercial District */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.commercial_district = el)}
                options={options.commercial_district}
                value={formState.commercial_district || null}
                onChange={(e, value) => handleAutocompleteChange('commercial_district', value)}
                renderInput={(params) => (
                  <TextField {...params} label="Commercial District" fullWidth />
                )}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Commercial City */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.commercial_city = el)}
                options={options.commercial_city}
                value={formState.commercial_city || null}
                onChange={(e, value) => handleAutocompleteChange('commercial_city', value)}
                renderInput={(params) => (
                  <TextField {...params} label="Commercial City" fullWidth />
                )}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* MBU */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.mbu = el)}
                options={options.mbu}
                value={formState.mbu || null}
                onChange={(e, value) => handleAutocompleteChange('mbu', value)}
                renderInput={(params) => <TextField {...params} label="MBU" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Franchise ID */}
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Franchise ID"
                name="franchise_id"
                type="number"
                value={formState.franchise_id || ''}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            {/* Site Type */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.site_type = el)}
                options={options.site_type}
                value={formState.site_type || null}
                onChange={(e, value) => handleAutocompleteChange('site_type', value)}
                renderInput={(params) => <TextField {...params} label="Site Type" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Sharing Type (Host/Guest) */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.sharing_type_hostguest = el)}
                options={options.sharing_type_hostguest}
                value={formState.sharing_type_hostguest || null}
                onChange={(e, value) => handleAutocompleteChange('sharing_type_hostguest', value)}
                renderInput={(params) => (
                  <TextField {...params} label="Sharing Type (Host/Guest)" fullWidth />
                )}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* OMO Name */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.omo_name = el)}
                options={options.omo_name}
                value={formState.omo_name || null}
                onChange={(e, value) => handleAutocompleteChange('omo_name', value)}
                renderInput={(params) => <TextField {...params} label="OMO Name" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* B2S ID */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.b2s_id = el)}
                options={options.b2s_id}
                value={formState.b2s_id || null}
                onChange={(e, value) => handleAutocompleteChange('b2s_id', value)}
                renderInput={(params) => <TextField {...params} label="B2S ID" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* BSC */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.bsc = el)}
                options={options.bsc}
                value={formState.bsc || null}
                onChange={(e, value) => handleAutocompleteChange('bsc', value)}
                renderInput={(params) => <TextField {...params} label="BSC" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* BSC Code */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.bsc_code = el)}
                options={options.bsc_code}
                value={formState.bsc_code || null}
                onChange={(e, value) => handleAutocompleteChange('bsc_code', value)}
                renderInput={(params) => <TextField {...params} label="BSC Code" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* LTE Technology */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.lte_technology = el)}
                options={options.lte_technology}
                value={formState.lte_technology || null}
                onChange={(e, value) => handleAutocompleteChange('lte_technology', value)}
                renderInput={(params) => <TextField {...params} label="LTE Technology" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            {/* Deodar Tag */}
            <Grid item xs={4}>
              <Autocomplete
                ref={(el) => (autocompleteRefs.current.deodar_tag = el)}
                options={options.deodar_tag}
                value={formState.deodar_tag || null}
                onChange={(e, value) => handleAutocompleteChange('deodar_tag', value)}
                renderInput={(params) => <TextField {...params} label="Deodar Tag" fullWidth />}
                size="small"
                blurOnSelect={false}
                clearOnBlur={false}
                selectOnFocus={false}
                freeSolo={false}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent={'end'} gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  sx={{
                    borderColor: '#2B323B',
                    color: '#2B323B',
                  }}
                >
                  Clear
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!formState.site_code || formState.site_code.trim() === ''}
                  sx={{
                    backgroundColor: '#BD1D23',
                    color: '#ffffff',
                    fontWeight: 600,
                    padding: '8px 24px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    boxShadow: '0 2px 4px rgba(189, 29, 35, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#a0181e',
                      boxShadow: '0 4px 8px rgba(189, 29, 35, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 2px 4px rgba(189, 29, 35, 0.3)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#f5f5f5',
                      color: '#9e9e9e',
                      boxShadow: 'none',
                      border: '1px solid #e0e0e0',
                      cursor: 'not-allowed',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        transform: 'none',
                        boxShadow: 'none',
                      },
                    },
                  }}
                >
                  {isEditMode ? 'Update' : 'Save'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};

export default RanDBAddSingleSiteComponent;
